import React, { useState } from 'react';
import { MapPin, Mail, Lock, User, Building, Phone, Eye, EyeOff, AlertCircle, CheckCircle } from 'lucide-react';
import { AuthService } from '../services/authService';
import { validateEmail, validatePhone } from '../utils/helpers';

// Composant InputField défini EN DEHORS du composant Login pour éviter les re-renders
const InputField = ({ icon: Icon, label, type, value, onChange, error, placeholder, name, showToggle, onToggle, showValue }) => (
  <div className="mb-4">
    <label className="block text-sm font-medium text-slate-700 mb-1">{label}</label>
    <div className="relative">
      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
        <Icon className="h-5 w-5 text-slate-400" />
      </div>
      <input
        type={showToggle ? (showValue ? 'text' : 'password') : type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={`block w-full pl-10 pr-${showToggle ? '10' : '3'} py-2.5 border ${error ? 'border-red-500' : 'border-slate-300'
          } rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors`}
      />
      {showToggle && (
        <button
          type="button"
          onClick={onToggle}
          className="absolute inset-y-0 right-0 pr-3 flex items-center"
        >
          {showValue ? (
            <EyeOff className="h-5 w-5 text-slate-400 hover:text-slate-600" />
          ) : (
            <Eye className="h-5 w-5 text-slate-400 hover:text-slate-600" />
          )}
        </button>
      )}
    </div>
    {error && (
      <p className="mt-1 text-sm text-red-500 flex items-center">
        <AlertCircle className="h-4 w-4 mr-1" />
        {error}
      </p>
    )}
  </div>
);

const Login = ({ onLogin }) => {
  const [activeTab, setActiveTab] = useState('login');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // États connexion
  const [loginData, setLoginData] = useState({
    email: '',
    password: ''
  });
  const [loginErrors, setLoginErrors] = useState({});

  // États inscription
  const [registerData, setRegisterData] = useState({
    username: '',
    email: '',
    bureau: '',
    telephone: '',
    password: '',
    confirmPassword: ''
  });
  const [registerErrors, setRegisterErrors] = useState({});

  // État mot de passe oublié
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');

  const validateLoginForm = () => {
    const errors = {};
    if (!loginData.email) {
      errors.email = 'L\'email est requis';
    } else if (!validateEmail(loginData.email)) {
      errors.email = 'Format d\'email invalide';
    }
    if (!loginData.password) {
      errors.password = 'Le mot de passe est requis';
    }
    setLoginErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateRegisterForm = () => {
    const errors = {};
    if (!registerData.username || registerData.username.length < 3) {
      errors.username = 'Le nom d\'utilisateur doit contenir au moins 3 caractères';
    }
    if (!registerData.email) {
      errors.email = 'L\'email est requis';
    } else if (!validateEmail(registerData.email)) {
      errors.email = 'Format d\'email invalide';
    }
    if (registerData.telephone && !validatePhone(registerData.telephone)) {
      errors.telephone = 'Format de téléphone invalide (+212 6XX XXX XXX)';
    }
    if (!registerData.password) {
      errors.password = 'Le mot de passe est requis';
    } else if (registerData.password.length < 6) {
      errors.password = 'Le mot de passe doit contenir au moins 6 caractères';
    }
    if (registerData.password !== registerData.confirmPassword) {
      errors.confirmPassword = 'Les mots de passe ne correspondent pas';
    }
    setRegisterErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!validateLoginForm()) return;

    setLoading(true);
    setMessage({ type: '', text: '' });

    const result = await AuthService.signIn(loginData.email, loginData.password);

    if (result.success) {
      setMessage({ type: 'success', text: 'Connexion réussie!' });
      if (onLogin) onLogin(result.user);
    } else {
      setMessage({ type: 'error', text: result.error });
    }

    setLoading(false);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!validateRegisterForm()) return;

    setLoading(true);
    setMessage({ type: '', text: '' });

    const result = await AuthService.createAccount(registerData.email, registerData.password, {
      username: registerData.username,
      bureau: registerData.bureau,
      telephone: registerData.telephone
    });

    if (result.success) {
      setMessage({ type: 'success', text: 'Compte créé avec succès!' });
      if (onLogin) onLogin(result.user);
    } else {
      setMessage({ type: 'error', text: result.error });
    }

    setLoading(false);
  };

  const handleForgotPassword = async () => {
    if (!forgotEmail || !validateEmail(forgotEmail)) {
      setMessage({ type: 'error', text: 'Veuillez entrer un email valide' });
      return;
    }

    setLoading(true);
    const result = await AuthService.resetPassword(forgotEmail);

    if (result.success) {
      setMessage({ type: 'success', text: 'Email de réinitialisation envoyé!' });
      setShowForgotPassword(false);
      setForgotEmail('');
    } else {
      setMessage({ type: 'error', text: result.error });
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen lg:grid lg:grid-cols-2 bg-slate-50">
      {/* Panneau de marque (desktop) */}
      <section className="hidden lg:flex flex-col justify-between bg-gradient-to-br from-brand-500 via-brand-600 to-brand-800 text-white p-12 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.07] pointer-events-none"
          style={{ backgroundImage: 'linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)', backgroundSize: '48px 48px' }}
          aria-hidden="true"
        />
        <div className="absolute -bottom-32 -right-32 w-96 h-96 rounded-full bg-white/10 blur-3xl pointer-events-none" aria-hidden="true" />
        <div className="relative flex items-center gap-3">
          <div className="bg-white/15 p-2.5 rounded-xl">
            <MapPin className="h-6 w-6 text-white" />
          </div>
          <span className="font-heading font-bold text-lg">Suivi des Affaires</span>
        </div>
        <div className="relative max-w-md">
          <h1 className="font-heading text-4xl xl:text-5xl font-extrabold leading-tight">
            Pilotez vos dossiers d'architecture, du dépôt à la fin des travaux.
          </h1>
          <p className="mt-5 text-brand-100 text-lg leading-relaxed">
            Suivi des affaires, clients, honoraires et parcelles géoréférencées (Merchich) — pour les cabinets d'architectes au Maroc.
          </p>
          <ul className="mt-8 space-y-3 text-sm text-brand-100">
            {['Workflow des statuts et historique', 'Comptabilité, avances et reçus PDF', 'Comptes agents : architecte, secrétaire, technicien', 'Carte interactive des parcelles'].map(item => (
              <li key={item} className="flex items-center gap-3">
                <span className="w-1.5 h-1.5 rounded-full bg-accent-500" aria-hidden="true" />
                {item}
              </li>
            ))}
          </ul>
        </div>
        <p className="relative text-xs text-brand-100">© {new Date().getFullYear()} METATOPO</p>
      </section>

      {/* Formulaire */}
      <div className="flex flex-col justify-center px-4 py-10 sm:px-8 lg:px-16">
      <div className="mx-auto w-full max-w-md">
        <div className="flex items-center gap-3 lg:hidden mb-8">
          <div className="bg-brand-600 p-2.5 rounded-xl shadow-sm">
            <MapPin className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="font-heading text-xl font-bold text-slate-900 leading-tight">Suivi des Affaires</h1>
            <p className="text-sm text-slate-500">Cabinets d'architectes · Maroc</p>
          </div>
        </div>
        <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">
          {activeTab === 'login' ? 'Bon retour' : 'Créer votre cabinet'}
        </h2>
        <p className="mt-1.5 text-sm text-slate-500">
          {activeTab === 'login' ? 'Connectez-vous pour accéder à vos affaires.' : 'Le compte créé devient l\'architecte propriétaire du cabinet.'}
        </p>
      </div>

      <div className="mt-8 mx-auto w-full max-w-md">
        <div className="card py-7 px-5 sm:px-8">
          {/* Tabs */}
          <div className="flex mb-6 bg-slate-100 rounded-xl p-1" role="tablist">
            <button
              role="tab"
              aria-selected={activeTab === 'login'}
              onClick={() => {
                setActiveTab('login');
                setMessage({ type: '', text: '' });
              }}
              className={`flex-1 min-h-[44px] text-sm font-medium rounded-lg transition-all duration-200 ${activeTab === 'login'
                  ? 'bg-white text-brand-800 shadow-card'
                  : 'text-slate-500 hover:text-slate-800'
                }`}
            >
              Connexion
            </button>
            <button
              role="tab"
              aria-selected={activeTab === 'register'}
              onClick={() => {
                setActiveTab('register');
                setMessage({ type: '', text: '' });
              }}
              className={`flex-1 min-h-[44px] text-sm font-medium rounded-lg transition-all duration-200 ${activeTab === 'register'
                  ? 'bg-white text-brand-800 shadow-card'
                  : 'text-slate-500 hover:text-slate-800'
                }`}
            >
              Inscription
            </button>
          </div>

          {/* Message */}
          {message.text && (
            <div className={`mb-4 p-3 rounded-lg flex items-center ${message.type === 'error'
                ? 'bg-red-50 text-red-700 border border-red-200'
                : 'bg-green-50 text-green-700 border border-green-200'
              }`}>
              {message.type === 'error' ? (
                <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0" />
              ) : (
                <CheckCircle className="h-5 w-5 mr-2 flex-shrink-0" />
              )}
              <span className="text-sm">{message.text}</span>
            </div>
          )}

          {/* Formulaire Connexion */}
          {activeTab === 'login' && !showForgotPassword && (
            <form onSubmit={handleLogin}>
              <InputField
                icon={Mail}
                label="Email"
                type="email"
                name="email"
                value={loginData.email}
                onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                error={loginErrors.email}
                placeholder="votre@email.com"
              />

              <InputField
                icon={Lock}
                label="Mot de passe"
                type="password"
                name="password"
                value={loginData.password}
                onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                error={loginErrors.password}
                placeholder="••••••••"
                showToggle
                onToggle={() => setShowPassword(!showPassword)}
                showValue={showPassword}
              />

              <div className="flex items-center justify-end mb-4">
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="text-sm text-brand-700 hover:text-brand-600"
                >
                  Mot de passe oublié?
                </button>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full !min-h-[46px]"
              >
                {loading ? (
                  <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Connexion...
                  </span>
                ) : (
                  'Se connecter'
                )}
              </button>
            </form>
          )}

          {/* Formulaire Mot de passe oublié */}
          {showForgotPassword && (
            <div>
              <h3 className="text-lg font-medium text-slate-900 mb-4">Réinitialiser le mot de passe</h3>
              <InputField
                icon={Mail}
                label="Email"
                type="email"
                name="forgotEmail"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                placeholder="votre@email.com"
              />
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(false)}
                  className="flex-1 py-2.5 px-4 border border-slate-300 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="button"
                  onClick={handleForgotPassword}
                  disabled={loading}
                  className="flex-1 py-2.5 px-4 border border-transparent rounded-xl shadow-card text-sm font-medium text-white bg-brand-600 hover:bg-brand-600 disabled:opacity-50 transition-colors"
                >
                  {loading ? 'Envoi...' : 'Envoyer'}
                </button>
              </div>
            </div>
          )}

          {/* Formulaire Inscription */}
          {activeTab === 'register' && (
            <form onSubmit={handleRegister}>
              <InputField
                icon={User}
                label="Nom d'utilisateur *"
                type="text"
                name="username"
                value={registerData.username}
                onChange={(e) => setRegisterData({ ...registerData, username: e.target.value })}
                error={registerErrors.username}
                placeholder="Votre nom"
              />

              <InputField
                icon={Mail}
                label="Email *"
                type="email"
                name="email"
                value={registerData.email}
                onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                error={registerErrors.email}
                placeholder="votre@email.com"
              />

              <InputField
                icon={Building}
                label="Bureau d'architecte"
                type="text"
                name="bureau"
                value={registerData.bureau}
                onChange={(e) => setRegisterData({ ...registerData, bureau: e.target.value })}
                placeholder="Nom du bureau (optionnel)"
              />

              <InputField
                icon={Phone}
                label="Téléphone"
                type="tel"
                name="telephone"
                value={registerData.telephone}
                onChange={(e) => setRegisterData({ ...registerData, telephone: e.target.value })}
                error={registerErrors.telephone}
                placeholder="+212 6XX XXX XXX (optionnel)"
              />

              <InputField
                icon={Lock}
                label="Mot de passe *"
                type="password"
                name="password"
                value={registerData.password}
                onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                error={registerErrors.password}
                placeholder="••••••••"
                showToggle
                onToggle={() => setShowPassword(!showPassword)}
                showValue={showPassword}
              />

              <InputField
                icon={Lock}
                label="Confirmer le mot de passe *"
                type="password"
                name="confirmPassword"
                value={registerData.confirmPassword}
                onChange={(e) => setRegisterData({ ...registerData, confirmPassword: e.target.value })}
                error={registerErrors.confirmPassword}
                placeholder="••••••••"
                showToggle
                onToggle={() => setShowConfirmPassword(!showConfirmPassword)}
                showValue={showConfirmPassword}
              />

              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full mt-4 !min-h-[46px]"
              >
                {loading ? (
                  <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Création...
                  </span>
                ) : (
                  'Créer le compte'
                )}
              </button>
            </form>
          )}
        </div>

        {/* Footer */}
        <div className="mt-6 text-center text-xs text-slate-400">
          <p>Application développée par METATOPO ·{' '}
            <a href="mailto:Bouhabba.igt@gmail.com" className="text-slate-500 hover:text-brand-700">Bouhabba.igt@gmail.com</a>
            {' · '}
            <a href="tel:+212661245376" className="text-slate-500 hover:text-brand-700">+212 661 245 376</a>
          </p>
        </div>
      </div>
      </div>
    </div>
  );
};

export default Login;
